import { useEffect } from "react";
import { Link } from "react-router-dom-v5-compat";
import { FormCheckbox, FormTextField } from "@apac/form-ui";
import type { FlightKey } from "@b2bportal/air-price-watch-api";
import { TRIPS_PATH } from "@b2bportal/core-utilities";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  useWatchTripForm,
  type WatchTripResponse,
} from "./hooks/useWatchTripForm";

export interface WatchTripFormState {
  isValid: boolean;
  isSubmitting: boolean;
}

interface WatchTripFormProps {
  cn: (elementName: string) => string;
  flightKey: FlightKey;
  onSuccess: (response: WatchTripResponse) => void;
  onError?: (error: unknown) => void;
  onFormStateChange?: (state: WatchTripFormState) => void;
}

export const WatchTripForm = ({
  cn,
  flightKey,
  onSuccess,
  onError,
  onFormStateChange,
}: WatchTripFormProps): JSX.Element => {
  const {
    t,
    brand: { urls },
  } = useI18nContext();

  const {
    context: { form },
    handlers: { onWatchTripSubmit },
    componentProps: {
      nonstopInputProps,
      noBasicFareInputProps,
      emailInputProps,
    },
  } = useWatchTripForm({
    flightKey,
    onSuccess: (response) => {
      form.reset();
      onSuccess?.(response);
    },
    onError: (error) => {
      form.reset();
      onError?.(error);
    },
  });

  const { isValid, isSubmitting } = form.formState;

  useEffect(() => {
    onFormStateChange?.({ isValid, isSubmitting });
  }, [isValid, isSubmitting]);

  return (
    <div className={cn("watch-trip-view")}>
      <p className={cn("subtitle")}>{t("priceWatch.titles.title")}</p>
      <p className={cn("description")}>{t("priceWatch.titles.subtitle")}</p>

      <form
        id="watch-trip-form"
        className={cn("form")}
        onSubmit={onWatchTripSubmit}
      >
        <FormCheckbox
          label={t("priceWatch.directFlightsOnly")}
          {...nonstopInputProps}
        />
        <FormCheckbox
          label={t("priceWatch.excludeBasicFares")}
          {...noBasicFareInputProps}
        />
        <FormTextField
          label={t("email")}
          {...emailInputProps}
          type="email"
          autoComplete="email"
        />
        <p className={cn("disclaimer")}>
          <Trans
            className={cn("disclaimer-content")}
            i18nKey="priceWatch.titles.description"
            components={{
              terms: (
                <Link
                  to={urls.pricePredictionTerms}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              trips: <Link className={cn("trips-link")} to={TRIPS_PATH} />,
            }}
          />
        </p>
      </form>
    </div>
  );
};
